import { FC } from 'react';

import { buildSanityImageUrl } from '@lib/sanity/image';
import type { SanityImage } from '@lib/sanity/types';

/** Cobalt disc behind the initial, shared by the hero lockup and endorsement. */
const INITIAL_BACKGROUND_COLOUR = '#0a4ab8';
/** Request 2x the rendered size for high-density screens. */
const PORTRAIT_DENSITY_MULTIPLIER = 2;

const getInitial = (name: string): string =>
  name.trim().charAt(0).toUpperCase();

type CreatorAvatarProps = {
  name?: string;
  portrait?: SanityImage;
  /** Rendered square size in px; drives both layout and the fetched width. */
  renderedSize: number;
  /** Tailwind text size class for the initial, e.g. 'text-lg'. */
  initialTextClassName?: string;
  ringWidth?: number;
};

/**
 * The creator's circular mark: their portrait when uploaded, otherwise their
 * initial on a cobalt disc. Renders nothing when there is neither a portrait
 * nor a name, so a half-authored page never shows a placeholder initial.
 */
const CreatorAvatar: FC<CreatorAvatarProps> = ({
  name,
  portrait,
  renderedSize,
  initialTextClassName = 'text-lg',
  ringWidth = 2
}) => {
  // The accent ring reads the page-level custom property set by the renderer.
  const ringStyle = { boxShadow: `0 0 0 ${ringWidth}px var(--accent)` };
  const sizeStyle = { width: renderedSize, height: renderedSize };

  if (portrait) {
    return (
      <img
        src={buildSanityImageUrl(portrait)
          .width(renderedSize * PORTRAIT_DENSITY_MULTIPLIER)
          .quality(80)
          .url()}
        alt={name ?? ''}
        width={renderedSize}
        height={renderedSize}
        loading="lazy"
        decoding="async"
        className="shrink-0 rounded-full object-cover"
        style={{ ...ringStyle, ...sizeStyle }}
      />
    );
  }

  const trimmedName = name?.trim();
  if (!trimmedName) return null;

  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full font-bold text-white ${initialTextClassName}`}
      style={{
        backgroundColor: INITIAL_BACKGROUND_COLOUR,
        ...ringStyle,
        ...sizeStyle
      }}
    >
      {getInitial(trimmedName)}
    </span>
  );
};

export default CreatorAvatar;
